import {
  Wallet,
  ArrowUpCircle,
  Users,
  Heart,
  Star,
  Trophy,
  Dices,
  Lightbulb,
  Gift,
  Inbox,
  Radio,
} from "lucide-react";
import { formatAppDate } from "./appTimeFormat";

export const ACTIVITY_FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "bets", label: "Bets" },
  { id: "wallet", label: "Wallet" },
  { id: "p2p", label: "P2P" },
  { id: "rewards", label: "Rewards" },
];

export function formatAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 45) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return "Yesterday";
  return `${Math.floor(s / 86400)}d ago`;
}

function sameDay(a, b) {
  return a.toDateString() === b.toDateString();
}

export function formatTimelineSectionTitle(dateKey) {
  if (dateKey === "unknown") return "Earlier";
  const d = new Date(`${dateKey}T12:00:00`);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return formatAppDate(d);
}

export function buildTimelineSections(rows, getDateKey) {
  const bucket = new Map();
  for (const row of rows || []) {
    const key = getDateKey(row) || "unknown";
    if (!bucket.has(key)) bucket.set(key, []);
    bucket.get(key).push(row);
  }
  return Array.from(bucket.entries()).map(([dateKey, data]) => ({
    dateKey,
    title: formatTimelineSectionTitle(dateKey),
    data,
  }));
}

export function getActivityDateKey(item) {
  if (!item?.createdAt) return "unknown";
  const d = new Date(item.createdAt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function matchesActivityFilter(item, filterId) {
  const a = String(item?.action || "").toLowerCase();
  switch (filterId) {
    case "all":
      return true;
    case "unread":
      return !item.isRead;
    case "bets":
      return (
        a.includes("bet") ||
        a.includes("market") ||
        a.includes("ticket") ||
        a.includes("bethouse") ||
        a.includes("house_")
      );
    case "wallet":
      return (
        a.includes("deposit") ||
        a.includes("withdraw") ||
        a.includes("credit") ||
        a.includes("payout") ||
        a.includes("conversion") ||
        a.includes("cash") ||
        a.includes("coin")
      );
    case "p2p":
      return a.includes("p2p") || a.includes("invite");
    case "rewards":
      return a.includes("reward") || a.includes("level") || a.includes("leaderboard");
    default:
      return true;
  }
}

export function accentForAction(action) {
  const a = String(action || "").toLowerCase();
  if (a.includes("deposit") || a.includes("credit")) {
    return { color: "#FFCA28", Icon: Wallet, label: "Wallet" };
  }
  if (a.includes("withdraw") || a.includes("payout")) {
    return { color: "#2ECC71", Icon: ArrowUpCircle, label: "Payout" };
  }
  if (a.includes("p2p_invite")) {
    return { color: "#64B5F6", Icon: Inbox, label: "P2P invite" };
  }
  if (a.includes("p2p_favorite")) {
    return { color: "#FFD54F", Icon: Star, label: "Favorite team" };
  }
  if (a.includes("p2p_following")) {
    return { color: "#64B5F6", Icon: Heart, label: "Following" };
  }
  if (a.includes("bethouse_following")) {
    return { color: "#66BB6A", Icon: Radio, label: "House live" };
  }
  if (a.includes("prediction_favorite_team")) {
    return { color: "#FFB74D", Icon: Star, label: "Favorite team" };
  }
  if (a.includes("prediction_following")) {
    return { color: "#FFB74D", Icon: Lightbulb, label: "Following" };
  }
  if (a.includes("p2p") || a.includes("invite")) {
    return { color: "#64B5F6", Icon: Users, label: "P2P" };
  }
  if (a.includes("bet") || a.includes("market") || a.includes("ticket")) {
    return { color: "#1E88E5", Icon: Trophy, label: "Bet" };
  }
  if (a.includes("casino") || a.includes("game")) {
    return { color: "#4FC3F7", Icon: Dices, label: "Play" };
  }
  if (a.includes("prediction")) {
    return { color: "#FFB74D", Icon: Lightbulb, label: "Predict" };
  }
  if (a.includes("reward") || a.includes("level")) {
    return { color: "#2ECC71", Icon: Gift, label: "Reward" };
  }
  return { color: "rgba(255,255,255,0.55)", Icon: Radio, label: "Update" };
}

export function formatActionLabel(action) {
  if (!action) return "event";
  return String(action).replace(/_/g, " ");
}
